import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Key,
  Waves,
  MapPin,
  Star,
  ArrowRight,
  Building
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const CocoaBeachPage = () => {
  const services = [
    {
      title: "Vacation Rental Cleaning",
      description: "Fast, reliable turnovers for Cocoa Beach Airbnb, VRBO, and vacation rental properties. Guest-ready standards that protect your reviews."
    },
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for year-round Cocoa Beach residents. We understand beachside living and the challenges it brings."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for beachfront homes dealing with sand, salt air, and high-traffic vacation rental turnover."
    },
    {
      title: "Commercial Cleaning",
      description: "Professional cleaning for Cocoa Beach businesses, surf shops, rental offices, and hospitality properties."
    }
  ];

  const perfectFor = [
    "Vacation rental property owners and managers",
    "Beachfront homeowners managing sand and salt air",
    "Year-round residents in North and South Cocoa Beach",
    "Businesses serving the tourist and surf community",
    "Property management companies handling multiple units",
    "Families living near the pier or Minuteman Causeway"
  ];

  const faqs = [
    {
      question: "Can you handle same-day vacation rental turnovers in Cocoa Beach?",
      answer: "Yes. We do same-day turnovers regularly for Cocoa Beach vacation rentals. Tourism is a big part of this community, and we understand the tight timelines. Give us as much notice as possible, and we'll coordinate to meet your check-in schedule."
    },
    {
      question: "Do you serve both North and South Cocoa Beach?",
      answer: "Absolutely. We serve all of Cocoa Beach—from the areas near the pier and Minuteman Causeway to North Cocoa Beach and the neighborhoods toward Cape Canaveral. We're familiar with the entire area."
    },
    {
      question: "How do you handle sand and beach traffic in vacation rentals?",
      answer: "It's part of the territory in Cocoa Beach. We focus extra attention on floors, entryways, showers, and any high-traffic areas. Regular turnovers prevent the kind of buildup that damages finishes and shows up in guest reviews."
    },
    {
      question: "What makes Pierce Premier different from other Cocoa Beach cleaning companies?",
      answer: "We're locally owned by Bob and Heather—not a franchise. You'll work directly with us, and you'll have the same cleaners handling your properties. That consistency matters when you're managing vacation rentals or running a business."
    },
    {
      question: "Can you clean commercial properties in Cocoa Beach?",
      answer: "Yes. We work with businesses throughout Cocoa Beach—surf shops, rental offices, small hospitality properties, and more. We're flexible and understand the demands of serving the tourist community."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Cocoa Beach, FL | Pierce Premier",
      description: "Vacation rental and residential cleaning in Cocoa Beach, FL. Fast turnovers, beachside specialists. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/cocoa-beach"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Cocoa Beach" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning and Vacation Rental Services in Cocoa Beach",
      description: "Professional vacation rental turnover, residential cleaning, and commercial cleaning services in Cocoa Beach, FL."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Cocoa Beach, FL | Pierce Premier"
        description="Vacation rental & residential cleaning in Cocoa Beach, FL. Fast turnovers, beachside specialists. Same team, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/cocoa-beach"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-orange-600" />
                  <Badge className="bg-orange-600 text-white">Surf City, USA</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Cocoa Beach, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Cocoa Beach is known for surf culture, tourism, and beautiful beachfront properties. Pierce Premier serves this unique community with cleaning services built for vacation rentals, year-round residents, and businesses that keep this coastal town running.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you manage vacation rentals near the pier, own a beachfront home, or run a business serving tourists and locals alike, we bring the reliability and professionalism you need.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-orange-600 text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80" 
                  alt="Vacation rental and house cleaning in Cocoa Beach, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Cocoa Beach Property Owners Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Key className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Vacation Rental Expertise</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather's 15+ years of experience includes Airbnb hosting herself. She knows what guests notice, what affects reviews, and how to deliver guest-ready turnovers consistently.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Waves className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Beachside Living Specialists</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Sand, salt air, high traffic—we've cleaned plenty of Cocoa Beach properties and know how to manage the unique challenges of living and renting near the ocean.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team, Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. Whether you have one vacation rental or manage multiple properties, you'll have the same cleaners handling your turnovers. That consistency protects your reputation.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Reliable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work and responds when you need us.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Cocoa Beach
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cocoa Beach has this interesting mix—tourists and surfers alongside families who've lived here for decades. It's a working beach town with personality, and we appreciate serving both the vacation rental market and the year-round residents who call this place home.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're managing rentals near the pier or raising a family in North Cocoa Beach, we're glad to be part of keeping your property clean and guest-ready.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Cocoa Beach Properties
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-orange-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For Cocoa Beach
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-orange-100">
                  <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Cocoa Beach Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Cocoa Beach Pier Area",
                "North Cocoa Beach",
                "South Cocoa Beach",
                "Minuteman Causeway",
                "A1A Beachside",
                "Cape Canaveral Border"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Code:</span> 32931
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Cape Canaveral", link: "#" },
                { name: "Merritt Island", link: "#" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" },
                { name: "Rockledge", link: "#" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Cocoa", link: "#" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200 hover:border-orange-400 transition-colors text-center group">
                    <span className="text-orange-700 font-semibold group-hover:text-orange-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-orange-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Cocoa Beach Clients
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Protect Your Cocoa Beach Property?"
          description="Fast turnovers, reliable service, and the quality your vacation rental or home deserves. Let Pierce Premier take care of the cleaning."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default CocoaBeachPage;